/**
 * view_promotion / select_promotion — banner and offer tracking. (#161, GA4-E4-06)
 *
 * We spend design and merchandising effort on banners and offer strips with no data on whether they
 * are seen or clicked, and no way to attribute revenue to a promotion.
 *
 * CONTRACT, emitted by Liquid (snippets/analytics-promo-attrs.liquid) on the element that IS the
 * creative — not the button inside it, so an impression means the creative was seen:
 *   [data-gtm-promo-id]        required — stable, copy-independent
 *   [data-gtm-promo-name]      required
 *   [data-gtm-creative-slot]   required — e.g. announcement_bar, home_hero_1
 *   [data-gtm-creative-name]   required
 *   [data-gtm-promo-items]     optional — HTML-escaped JSON array from analytics-item.liquid,
 *                              only where the promo names specific products
 *
 * 🔴 WHY AUTO-ROTATING CAROUSELS CANNOT INFLATE IMPRESSIONS (#161 step 7)
 * A promotion is counted at most ONCE per page view, keyed on promotion_id, and the element is
 * unobserved the moment it is counted. So an announcement bar that rewinds every 4 seconds reports
 * each of its three messages once and then nothing, no matter how long the page is left open — the
 * acceptance criterion's "leave it open for 2 minutes" test. This is a dedupe rather than a
 * throttle on purpose: a throttle still grows without bound, just more slowly, and the resulting
 * impression count would be a function of dwell time rather than of reach.
 *
 * Slides that are scrolled out of a carousel do not count until they are actually shown, because
 * IntersectionObserver clips the target rectangle by ancestor `overflow` — the inactive slides of a
 * Swiper are genuinely not intersecting, so "once per slide when the slide becomes active" (step 2)
 * falls out of the same mechanism rather than needing carousel-specific code. That matters: this
 * theme has at least three different slider implementations.
 *
 * WHY select_promotion IS DELEGATED
 * Promos live in sections that re-render (announcement bar, AJAX-swapped banners) and inside
 * carousels that clone slides. A delegated listener on the document needs no re-binding, and
 * `closest()` means a click on the button, the heading or the image all resolve to the same promo.
 *
 * OVERLAP WITH THE GENERIC CLICK LAYER (#165 / GA4-E5-02)
 * Tracking plan §4 rules that clicks already covered by a GA4 recommended event are excluded from
 * the generic `data-gtm-click` layer. Promo slots therefore carry `data-gtm-promo-id` and must NOT
 * also carry `data-gtm-click`, or the click double-counts as both select_promotion and a generic
 * event. #165 has not shipped yet, so nothing double-counts today — this is the note that keeps it
 * that way.
 *
 * NO IntersectionObserver (very old browsers) => impressions are not reported, but clicks still are.
 * Reporting every promo as seen would be worse than reporting none: it silently inflates the CTR
 * denominator instead of leaving an obvious gap.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var SELECTOR = '[data-gtm-promo-id]';
  var VISIBLE_RATIO = 0.5;   // half the creative in view counts as seen
  var MUTATION_DEBOUNCE_MS = 100;   // coalesce a burst of DOM changes into one sweep

  var seen = Object.create(null);   // promotion_id -> true, once per page view

  function currency() {
    var dl = window.dataLayer || [];
    for (var i = dl.length - 1; i >= 0; i--) {
      var pd = dl[i] && dl[i].page_data;
      if (pd && pd.currency) return pd.currency;
    }
    return 'INR';
  }

  function promoOf(el) {
    var id = el.getAttribute('data-gtm-promo-id');
    if (!id) return null;

    var promo = {
      promotion_id: id,
      promotion_name: el.getAttribute('data-gtm-promo-name') || '',
      creative_slot: el.getAttribute('data-gtm-creative-slot') || '',
      creative_name: el.getAttribute('data-gtm-creative-name') || ''
    };

    var rawItems = el.getAttribute('data-gtm-promo-items');
    if (rawItems) {
      try {
        var items = JSON.parse(rawItems);
        if (items && items.length) promo.items = items;
      } catch (e) { /* a malformed blob must not cost us the event */ }
    }
    return promo;
  }

  function push(eventName, promo) {
    var ecommerce = {
      currency: currency(),
      promotion_id: promo.promotion_id,
      promotion_name: promo.promotion_name,
      creative_slot: promo.creative_slot,
      creative_name: promo.creative_name
    };
    if (promo.items) ecommerce.items = promo.items;

    // Clear the previous ecommerce object first. GA4 merges dataLayer state, so without this a later
    // event inherits this one's fields. Same guard as analytics-impressions.js (#156).
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
  }

  // ---------------------------------------------------------------- impressions
  if (window.IntersectionObserver) {
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;

        var el = entry.target;
        var promo = promoOf(el);

        // Stop watching regardless of whether it is a duplicate: this element has had its chance,
        // and re-entering the viewport is not a second impression.
        observer.unobserve(el);
        if (!promo || seen[promo.promotion_id]) continue;

        seen[promo.promotion_id] = true;
        push('view_promotion', promo);
      }
    }, { threshold: VISIBLE_RATIO });

    var observeAll = function (root) {
      var els = (root || document).querySelectorAll(SELECTOR);
      for (var i = 0; i < els.length; i++) {
        if (!seen[els[i].getAttribute('data-gtm-promo-id')]) observer.observe(els[i]);
      }
    };

    var startObserving = function () {
      observeAll(document);

      // Promos arrive after load in this theme — the announcement bar and several banner sections
      // are re-rendered by section AJAX, and sliders clone slides. Re-sweeping when the tree changes
      // keeps impressions honest without re-binding anything. The `seen` map means a cloned slide
      // cannot report its promo twice.
      //
      // Debounced, matching analytics-impressions.js (#156). The un-debounced version walked every
      // added node on every site-wide mutation and ran a querySelectorAll on each — a slider cloning
      // slides or a section AJAX swap adds nodes in bursts, so one visual change cost hundreds of
      // tree walks on the document. Coalescing to a single sweep per burst is the same work once.
      //
      // Sweeping the whole document rather than the added subtrees is what makes that safe: nothing
      // depends on catching an individual node, so a dropped mutation record costs nothing.
      // Re-observing an element IntersectionObserver already watches is a no-op per spec, and `seen`
      // still guards the counted ones, so repeated sweeps cannot double-report.
      if (window.MutationObserver) {
        var pending = null;
        new MutationObserver(function () {
          if (pending) return;
          pending = setTimeout(function () {
            pending = null;
            observeAll(document);
          }, MUTATION_DEBOUNCE_MS);
        }).observe(document.documentElement, { childList: true, subtree: true });
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startObserving);
    } else {
      startObserving();
    }
  }

  // ---------------------------------------------------------------- clicks
  // Capture phase, so the event is recorded even if a slider or popup handler calls
  // stopPropagation() on its own controls before it reaches the document in the bubble phase.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var el = target.closest(SELECTOR);
    if (!el) return;

    var promo = promoOf(el);
    if (!promo) return;

    push('select_promotion', promo);
  }, true);
})();
